// SubscriptionsManagement.js (исправленная версия)
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import ReactDOM from 'react-dom';
import {
  ArrowLeft,
  Calendar,
  Shield,
  Copy,
  Check,
  X,
  Smartphone,
  Trash2,
  Loader
} from 'lucide-react';
import { getSubscriptionStatus, getUser, getCountDevices, deleteDevice, renewSubscription } from '../api';
import '../styles/SubscriptionsManagement.css';

// Компонент карточки подписки (исправлен: трафик из getUser, убрана ошибка index)
function SubscriptionCard({ sub, onRenew, onHowToConnect, copyKeyToClipboard, copiedId, onManageDevices, onRenewSubscription }) {
  const [userData, setUserData] = useState(null);
  const [devices, setDevices] = useState(null);
  const [loadingDevices, setLoadingDevices] = useState(false);
  const [devicesError, setDevicesError] = useState(null);
  const [trafficData, setTrafficData] = useState(null);
  const [renewing, setRenewing] = useState(false);
  const navigate = useNavigate();

  const isBypass = sub.type === 'Обход глушилок';
  const isActive = sub.status === 'active';
  const username = sub.username;

  useEffect(() => {
    if (!isBypass || !isActive || !username) return;

    const fetchDevicesAndUser = async () => {
      setLoadingDevices(true);
      setDevicesError(null);
      try {
        const devicesData = await getCountDevices(username);
        setDevices(devicesData);

        const userInfo = await getUser(username);
        setUserData(userInfo);

        if (userInfo && userInfo.traffic_limit_bytes && userInfo.used_traffic_bytes) {
          const usedGB = userInfo.used_traffic_bytes / (1024 ** 3);
          const totalGB = userInfo.traffic_limit_bytes / (1024 ** 3);
          setTrafficData({
            used: parseFloat(usedGB.toFixed(1)),
            total: parseFloat(totalGB.toFixed(1))
          });
        } else {
          setTrafficData(null);
        }
      } catch (err) {
        console.error('Ошибка загрузки устройств/пользователя:', err);
        setDevicesError('Не удалось загрузить устройства');
      } finally {
        setLoadingDevices(false);
      }
    };

    fetchDevicesAndUser();
  }, [isBypass, isActive, username]);

  const totalDevices = devices?.total ?? devices?.devices?.length ?? 0;
  const currentTraffic = trafficData ?? sub.traffic;

  const handleRenewSubscription = async () => {
    if (!isBypass || !isActive) return;

    const confirmed = window.confirm(
      'Внимание!\n\n' +
      'Старый ключ перестанет работать.\n' +
      'Все подключённые устройства будут отключены.\n\n' +
      'Продолжить перевыпуск подписки?'
    );

    if (!confirmed) return;

    setRenewing(true);
    try {
      const newKey = await onRenewSubscription(sub.username || sub.telegramId || sub.id);
      
      alert('Подписка успешно перевыпущена!\n\nПожалуйста, скопируйте новый ключ во вкладке "мои подписки", вставьте его в Happ и подключитесь заново.');
      
      // Автоматически копируем новый ключ
      // await copyKeyToClipboard(newKey, sub.id);
      navigate('/');
      
    } catch (err) {
      console.error('Ошибка перевыпуска:', err);
      alert('Не удалось перевыпустить подписку: ' + (err.message || err));
    } finally {
      setRenewing(false);
    }
  };

  return (
    <motion.div
      className="sub-card"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      whileTap={{ scale: 0.98 }}
    >
      <div className="card-header">
        <h3 className="sub-type">{sub.type}</h3>
        <span className={`status-badge ${sub.status}`}>
          {sub.status === 'active' ? 'Активна' : 'Истекла'}
        </span>
      </div>

      <div className="sub-info">
        <div className="info-row">
          <Calendar size={18} />
          <span>Окончание: {sub.endDate}</span>
        </div>

        {currentTraffic && (
          <div className="traffic-section">
            <div className="traffic-header">
              <span className="traffic-label">Использовано трафика</span>
              <span className="traffic-values">
                {currentTraffic.used} / {currentTraffic.total} ГБ
              </span>
            </div>
            <div className="traffic-progress-container">
              {(() => {
                const usagePercent = (currentTraffic.used / currentTraffic.total) * 100;
                const fillClass = usagePercent > 90 ? 'danger' : usagePercent > 70 ? 'warning' : '';
                return (
                  <motion.div
                    className={`traffic-progress-fill ${fillClass}`}
                    initial={{ width: 0 }}
                    animate={{ width: `${usagePercent}%` }}
                    transition={{ duration: 0.8, ease: "easeOut" }}
                  />
                );
              })()}
            </div>
          </div>
        )}

        {isBypass && isActive && (
          <div className="devices-section">
            <div className="devices-info">
              <Smartphone size={18} />
              <span>
                {loadingDevices
                  ? 'Загрузка устройств...'
                  : devicesError
                  ? 'Ошибка загрузки'
                  : `Устройств: ${totalDevices}`}
              </span>
              {!loadingDevices && !devicesError && (
                <button
                  className="manage-devices-button"
                  onClick={() => onManageDevices(username)}
                >
                  Управление
                </button>
              )}
            </div>
            {devicesError && <div className="devices-error">{devicesError}</div>}
          </div>
        )}

        {isActive && sub.key && (
          <>
            <div className="instrukciya">
              <span>1. Скопируйте ключ</span>
            </div>
            <div className="info-row key-copy-row">
              <motion.button
                className="copy-key-full-button"
                whileTap={{ scale: 0.95 }}
                onClick={() => copyKeyToClipboard(sub.key, sub.id)}
              >
                {copiedId === sub.id ? (
                  <>
                    <Check size={18} />
                    <span>Ключ скопирован!</span>
                  </>
                ) : (
                  <>
                    <Copy size={18} />
                    <span>Скопировать ключ</span>
                  </>
                )}
              </motion.button>
            </div>
          </>
        )}

        {isActive && (
          <>
            <div className="instrukciya">
              <span>2. Скачайте Happ Proxy и вставьте ключ</span>
            </div>
            <div className="info-row app-buttons-row">
              {sub.type === 'Outline' && (
                <div className="app-buttons">
                  <a
                    href="https://itunes.apple.com/app/outline-app/id1356177741"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="app-button ios"
                  >
                    <span>iOS</span>
                  </a>
                  <a
                    href="https://play.google.com/store/apps/details?id=org.outline.android.client"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="app-button android"
                  >
                    <span>Android</span>
                  </a>
                </div>
              )}
              {(sub.type === 'Базовый' || sub.type === 'Обход глушилок') && (
                <div className="app-buttons-group">
                  <div className="app-buttons">
                    <a
                      href="https://apps.apple.com/app/happ-proxy-utility/id6504287215"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="app-button ios"
                    >
                      <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor">
                        <path d="M18.71 19.5C17.88 20.74 17 21.95 15.66 21.97C14.32 22 13.89 21.18 12.37 21.18C10.84 21.18 10.37 21.95 9.1 22C7.79 22.05 6.8 20.68 5.96 19.47C4.25 17 2.94 12.45 4.7 9.39C5.57 7.87 7.13 6.91 8.82 6.88C10.1 6.86 11.32 7.75 12.11 7.75C12.89 7.75 14.37 6.68 15.92 6.84C16.57 6.87 18.39 7.1 19.56 8.82C19.47 8.88 17.39 10.1 17.41 12.63C17.44 15.65 20.06 16.66 20.09 16.67C20.06 16.74 19.67 18.11 18.71 19.5ZM13 3.5C13.73 2.67 14.94 2.04 15.94 2C16.07 3.17 15.6 4.35 14.9 5.19C14.21 6.04 13.07 6.7 11.95 6.61C11.8 5.46 12.36 4.26 13 3.5Z"/>
                      </svg>
                      <span>Happ</span>
                    </a>

                    <a
                      href="https://play.google.com/store/apps/details?id=com.happproxy&hl=ru"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="app-button android"
                    >
                      <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor">
                        <path d="M17.6 9.48l1.84-3.18c.16-.31.04-.69-.26-.85-.29-.15-.65-.06-.83.22l-1.88 3.24A11.457 11.457 0 0 0 12 8.5c-1.53 0-2.98.3-4.32.84L5.8 6.1c-.19-.29-.55-.38-.84-.23-.3.16-.42.54-.26.85l1.84 3.18C4.36 10.97 3 13.3 3 16v1h18v-1c0-2.7-1.36-5.03-3.4-6.52zM7 14.5c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1zm10 0c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1z"/>
                      </svg>
                      <span>Happ</span>
                    </a>
                  </div>
                  <div className="incy-note">
                    <p className="incy-note-text">
                      Если Happ недоступен — смените регион в App Store на Казахстан.
                    </p>
                    {/* <a
                      href="https://apps.apple.com/ru/app/incy/id6756943388"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="app-button ios incy-button"
                    >
                      <span>Скачать Incy</span>
                    </a> */}
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {isBypass && isActive && (
          <div className="app-buttons">
            <motion.button
              className="renew-button danger renew-subscription-btn"
              whileTap={{ scale: 0.95 }}
              onClick={handleRenewSubscription}
              disabled={renewing}
            >
              {renewing ? (
                <>Перевыпуск...</>
              ) : (
                <>Перевыпустить ключ</>
              )}
            </motion.button>
          </div>
        )}

      <div className="buttons-row">
        <motion.button
          className={`renew-button ${sub.status === 'secondary' ? 'active' : ''}`}
          whileTap={{ scale: 0.95 }}
          onClick={() => onRenew(sub)}
        >
          Продлить
        </motion.button>
      </div>
    </motion.div>
  );
}

// Модальное окно управления устройствами (без изменений)
function DevicesManagementModal({ isOpen, onClose, telegramId }) {
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const loadDevices = async () => {
    if (!telegramId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await getCountDevices(telegramId);
      setDevices(data?.devices || []);
    } catch (err) {
      console.error('Ошибка загрузки устройств:', err);
      setError('Не удалось загрузить устройства');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && telegramId) {
      loadDevices();
    }
  }, [isOpen, telegramId]);

  const handleDeleteDevice = async (deviceId) => {
    if (!telegramId) return;
    const confirmDelete = window.confirm('Удалить это устройство?');
    if (!confirmDelete) return;

    try {
      await deleteDevice(telegramId, deviceId);
      await loadDevices();
    } catch (err) {
      console.error('Ошибка удаления устройства:', err);
      alert('Не удалось удалить устройство');
    }
  };

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <motion.div
      className="video-modal-overlay devices-modal-overlay"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
    >
      <motion.div
        className="video-modal-content devices-modal-content"
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.8, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
      >
        <button className="video-modal-close" onClick={onClose}>
          <X size={28} />
        </button>
        <h2 className="video-modal-title">Управление устройствами</h2>
        <div className="devices-list-container">
          {loading ? (
            <div className="devices-loading">
              <Loader size={24} className="spinner" />
              <span>Загрузка устройств...</span>
            </div>
          ) : error ? (
            <div className="devices-error">{error}</div>
          ) : devices.length === 0 ? (
            <div className="devices-empty">Нет подключённых устройств</div>
          ) : (
            <div className="devices-list">
              {devices.map((device) => (
                <div key={device.hwid} className="device-item">
                  <div className="device-icon">
                    <Smartphone size={24} />
                  </div>
                  <div className="device-details">
                    <div className="device-platform">
                      {device.platform || 'Неизвестная платформа'}
                    </div>
                    <div className="device-model">
                      {device.deviceModel || device.osVersion || 'Модель не указана'}
                    </div>
                    <div className="device-lastactive">
                      Последнее обновление: {new Date(device.updatedAt).toLocaleString()}
                    </div>
                  </div>
                  <button
                    className="device-delete-btn"
                    onClick={() => handleDeleteDevice(device.hwid)}
                    title="Удалить устройство"
                  >
                    <Trash2 size={20} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </motion.div>
    </motion.div>,
    document.body
  );
}

// Основной компонент SubscriptionsManagement
function SubscriptionsManagement({ setSelectedVPN, setSelectedMonths, vpnTypes }) {
  const navigate = useNavigate();
  const [telegramId, setTelegramId] = useState(null);
  const [isIOS, setIsIOS] = useState(false);
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [currentVideoType, setCurrentVideoType] = useState('');
  const [devicesModalOpen, setDevicesModalOpen] = useState(false);
  const [currentTelegramIdForDevices, setCurrentTelegramIdForDevices] = useState(null);

  const handleRenewSubscription = async (telegramId) => {
    if (!telegramId) throw new Error('Не указан telegram_id');

    const newKey = await renewSubscription(telegramId);
    return newKey; // возвращаем новый ключ
  };

  useEffect(() => {
    if (window.Telegram && window.Telegram.WebApp) {
      window.Telegram.WebApp.ready();
      window.Telegram.WebApp.expand();
      setIsIOS(window.Telegram.WebApp.platform === 'ios');
      const user = window.Telegram.WebApp.initDataUnsafe?.user;
      if (user?.id) setTelegramId(user.id);
    }
    if (process.env.NODE_ENV === 'development') {
      const params = new URLSearchParams(window.location.search);
      const tgIdFromUrl = params.get('tg_id');
      setTelegramId(tgIdFromUrl || 328036431);
    }
  }, []);

  useEffect(() => {
    if (!telegramId) return;

    const fetchSubscriptions = async () => {
      try {
        setLoading(true);
        setError(null);

        // 1. Получаем список подписок из API
        const data = await getSubscriptionStatus(telegramId);

        const typeMap = {
          dbaza: 'Outline',
          vless: 'Базовый',
          obhod: 'Обход глушилок',
        };

        // Фильтруем записи с валидным ключом
        const validItems = data.filter(item => {
          const key = item.key?.toString().trim();
          return key && key !== '0' && key !== '1';
        });

        // 2. Если есть подписка типа "Обход глушилок" – загружаем данные пользователя
        let bypassUserData = null;
        const hasBypass = validItems.some(item => item.table === 'obhod');
        if (hasBypass) {
          try {
            bypassUserData = await getUser(telegramId);
          } catch (err) {
            console.error('Ошибка загрузки данных пользователя для обхода глушилок:', err);
          }
        }

        // 3. Формируем массив подписок с корректным статусом
        const formatted = validItems.map((item, index) => {
          const type = typeMap[item.table] || item.table;
          let status = 'expired';
          let endDateFormatted = 'истек';
          let rawStop = item.stop || '';

          // Определяем статус в зависимости от типа
          if (type === 'Обход глушилок' && bypassUserData) {
            // Для обхода глушилок используем expire_at и трафик из getUser
            const expireDate = bypassUserData.expire_at ? new Date(bypassUserData.expire_at) : null;
            const hasTraffic = bypassUserData.traffic_limit_bytes && bypassUserData.used_traffic_bytes !== undefined;
            const trafficLeft = hasTraffic && (bypassUserData.used_traffic_bytes < bypassUserData.traffic_limit_bytes);

            if (expireDate && expireDate > new Date() && trafficLeft) {
              status = 'active';
            } else {
              status = 'expired';
            }

            // Форматируем дату окончания из expire_at
            if (expireDate) {
              endDateFormatted = expireDate.toLocaleDateString('ru-RU', {
                day: 'numeric',
                month: 'long',
                year: 'numeric'
              });
            }
          } else {
            // Для Outline и Базового – проверяем поле stop
            let stopDate = null;
            if (rawStop && typeof rawStop === 'string' && rawStop.trim() !== '') {
              const trimmed = rawStop.trim();
              if (trimmed.includes('.')) {
                const [day, month, year] = trimmed.split('.').map(Number);
                if (day && month && year) {
                  stopDate = new Date(year, month - 1, day);
                }
              } else {
                stopDate = new Date(trimmed);
              }
            }
            if (stopDate && !isNaN(stopDate.getTime()) && stopDate > new Date()) {
              status = 'active';
              endDateFormatted = stopDate.toLocaleDateString('ru-RU', {
                day: 'numeric',
                month: 'long',
                year: 'numeric'
              });
            }
          }

          return {
            id: `${item.table}-${index}`,
            type,
            status,
            endDate: endDateFormatted,
            rawStop: rawStop,
            key: item.key?.toString().trim(),
            username: telegramId,
            traffic: null, // для обхода трафик будет подтянут в карточке
          };
        });

        // 4. Оставляем только активные подписки
        const activeSubscriptions = formatted.filter(sub => sub.status === 'active');

        // Сортируем по дате окончания (от более поздней)
        activeSubscriptions.sort((a, b) => {
          const dateA = new Date(a.rawStop);
          const dateB = new Date(b.rawStop);
          return dateB - dateA;
        });

        setSubscriptions(activeSubscriptions);
      } catch (err) {
        console.error('Ошибка загрузки подписок:', err);
        setError('Не удалось загрузить подписки');
        setSubscriptions([]);
      } finally {
        setLoading(false);
      }
    };

    fetchSubscriptions();
  }, [telegramId]);

  const handleRenew = (sub) => {
    let targetVPN = null;
    if (sub.type === 'Outline') targetVPN = vpnTypes.find(v => v.id === 'outline');
    else if (sub.type === 'Базовый') targetVPN = vpnTypes.find(v => v.id === 'v2ray');
    else if (sub.type === 'Обход глушилок') targetVPN = vpnTypes.find(v => v.id === 'v2raybypass');
    if (targetVPN) {
      setSelectedVPN(targetVPN);
      setSelectedMonths('1');
    }
    navigate('/subscription');
  };

  const handleHowToConnect = (sub) => {
    setCurrentVideoType(sub.type === 'Outline' ? 'outline' : 'v2ray');
    setShowVideoModal(true);
  };

  const copyKeyToClipboard = async (key, subId) => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(key);
      } else {
        const textArea = document.createElement("textarea");
        textArea.value = key;
        textArea.style.position = "fixed";
        textArea.style.left = "-999999px";
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand("copy");
        document.body.removeChild(textArea);
      }
      setCopiedId(subId);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      alert("Не удалось скопировать ключ");
    }
  };

  const handleOpenDevicesModal = (username) => {
    setCurrentTelegramIdForDevices(username);
    setDevicesModalOpen(true);
  };

  const activeCount = subscriptions.length; // все подписки уже активны

  return (
    <div className="manage-wrapper">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="manage-content"
      >
        <button className="back-button" onClick={() => navigate(-1)}>
          <ArrowLeft size={24} />
        </button>
        <div className="manage-header">
          <Shield size={32} className="header-icon" />
          <h1 className="manage-title">Мои подписки</h1>
          <p className="manage-subtitle">
            {loading ? 'Загрузка...' : error ? 'Ошибка загрузки' : subscriptions.length > 0 ? `Активно: ${activeCount}` : 'У вас пока нет активных подписок'}
          </p>
        </div>
        <div className="subscriptions-list">
          {loading ? (
            <p className="empty-text">Загрузка подписок...</p>
          ) : error ? (
            <p className="empty-text error-text">{error}</p>
          ) : subscriptions.length === 0 ? (
            <p className="empty-text">Активные подписки не найдены. Выберите тариф на главном экране.</p>
          ) : (
            subscriptions.map((sub) => (
              <SubscriptionCard
                key={sub.id}
                sub={sub}
                onRenew={handleRenew}
                onHowToConnect={handleHowToConnect}
                copyKeyToClipboard={copyKeyToClipboard}
                copiedId={copiedId}
                onManageDevices={handleOpenDevicesModal}
                onRenewSubscription={handleRenewSubscription}
              />
            ))
          )}
        </div>
      </motion.div>

      <DevicesManagementModal
        isOpen={devicesModalOpen}
        onClose={() => setDevicesModalOpen(false)}
        telegramId={currentTelegramIdForDevices}
      />
    </div>
  );
}

export default SubscriptionsManagement;